<template>
    <!-- 계정 관리 — 쿠플러스 계정과 그 쿠팡 로그인을 **한 줄에서 같이** 관리한다(docs/multi-account-mcp-design.md §4).
         헤더 계정 아이콘을 누르면 바로 뜬다. 드롭다운은 없앴다. -->
    <b-modal
        id="coupangAccountModal"
        size="xl"
        dialog-class="account-manager-dialog"
        title="멀티계정관리"
        header-class="account-manager__head"
        @show="onShow"
        @hidden="stopShoppingPoll(); stopAliPoll()"
    >
        <!-- 머리줄 = 제목 · 함께 전환 스위치 · 닫기 (설명은 ? 에 올리면) -->
        <template #modal-header="{ close }">
            <h5 class="modal-title account-manager__title">멀티계정관리</h5>
            <!-- 쿠플러스·쿠팡 계정 함께 전환 — 확장이 브라우저별로 저장한다(기본 켜짐). 옛 확장·확장 없음이면 숨긴다 -->
            <div v-if="accountSync !== null" class="account-manager__sync">
                <b-form-checkbox switch :checked="accountSync" :disabled="accountSyncBusy" @change="toggleAccountSync">
                    쿠플러스·쿠팡 계정 함께 전환
                </b-form-checkbox>
                <span v-b-tooltip.hover class="account-manager__q" tabindex="0" role="img"
                      aria-label="한쪽 계정을 바꾸면 다른 쪽도 같은 계정으로 맞춥니다."
                      title="한쪽 계정을 바꾸면 다른 쪽도 같은 계정으로 맞춥니다.">?</span>
            </div>
            <button type="button" class="close" aria-label="닫기" @click="close()">&times;</button>
        </template>
        <div class="account-manager">
            <p v-if="loadError" class="account-manager__error" role="alert">{{ loadError }}</p>
            <!-- 고객 약속(판 #833) — 쿠팡 판매자·쇼핑 비번 = CLI 금고(AES-256-GCM) · 1688 = CLI 금고(_shared/1688-credentials.json · cpl-nego-cred1688-1 · 확장 사본 0) · 서버로 0.
                 «새로 저장하는» = 옛 판이 확장 저장소에 남긴 평문(background.js pwSealState.plainLeft)은 이 약속 밖 — 0 으로 만드는 일은 cpl-x3-plainseal-1(판 #846 · 사장님 11:57) -->
            <p class="account-manager__privacy">새로 저장하는 비밀번호는 이 PC 에만 암호화해 둡니다(서버로 보내지 않음)</p>

            <!-- 쿠팡 판매자 계정 — 계정(카드)마다 하나. 윙·서플라이어허브·광고센터 로그인에 쓴다(서플라이어허브 설정 화면 = 도구 KG Channel:supplier -setupAt).
                 ontology/build/tool.js 가 h6 글자와 바로 다음 줄의 사이트 주소를 읽는다 — 두 줄을 붙여 둔다 -->
            <div class="account-manager__sec">
                <h6 id="sellerAccountTitle" class="account-manager__sec-title">쿠팡 판매자 계정</h6>
                <span v-b-tooltip.hover class="account-manager__q" tabindex="0" role="img" aria-label="계정마다 supplier.coupang.com·wing.coupang.com 판매자 아이디·비밀번호를 넣고 저장합니다." title="계정마다 supplier.coupang.com·wing.coupang.com 판매자 아이디·비밀번호를 넣고 저장합니다.">?</span>
            </div>
            <div class="account-manager__cards" role="list" aria-labelledby="sellerAccountTitle">
                <p v-if="loading && !rows.length" class="account-manager__empty">불러오는 중</p>
                <CoupangAccountRow v-for="row in rows" :key="row.key" :row="row" role="listitem"
                                   :disabled="saving" :switch-blocked-reason="switchBlockedReason"
                                   :open-api="openApiFor(row)"
                                   @check="saveOne" @login-check="checkOne" @switch="switchTo" @remove="removeRow"
                                   @auto-collect="openAutoCollect" @open-api="openOpenApi" />
                <p v-if="!loading && !rows.length" class="account-manager__empty">계정이 없습니다. 아래에서 추가하세요.</p>
            </div>

            <button type="button" class="account-manager__add" :disabled="saving" @click="addRow">+ 줄 추가</button>
            <p v-if="switchBlockedReason" class="account-manager__sub mt-1 mb-0">{{ switchBlockedReason }}</p>

            <div class="account-manager__sec account-manager__sec--shared">
                <h6 class="account-manager__sec-title">모든 계정 공통 로그인</h6>
            </div>
            <!-- 공통 로그인도 판매자 계정처럼 한 줄씩(판 #833 · 사장님 10-31 00:27) — 칸 폭은 판매자 카드와 같다 -->
            <div class="account-manager__shared" role="list">
            <!-- 쿠팡 쇼핑 계정 — 판매자 계정과 달리 전체에서 하나만 쓰는 공통 계정 -->
            <section class="shopping-account" role="listitem" aria-labelledby="shoppingAccountTitle">
                <div class="shopping-account__head">
                <h6 id="shoppingAccountTitle" class="shopping-account__title">쿠팡 쇼핑</h6>
                <div class="shopping-account__sub" title="www.coupang.com 로그인 계정 하나 — 모든 계정이 같이 씁니다">상품검색·리뷰</div>
                </div>
                <div class="shopping-account__status">
                    <span :class="['shopping-account__bd', 'shopping-account__bd--' + shoppingLight.tone]" role="status">
                        <span v-if="toneMark[shoppingLight.tone]" aria-hidden="true">{{ toneMark[shoppingLight.tone] }}</span>
                        {{ shoppingLight.text }}
                    </span>
                </div>
                <div class="shopping-account__id">
                    <input type="text" class="form-control form-control-sm shopping-account__input"
                           :placeholder="shopping.status && shopping.status.id ? shopping.status.id : '쿠팡 쇼핑 아이디'"
                           :title="shopping.status && shopping.status.id ? '저장된 아이디 ' + shopping.status.id + ' — 바꿀 때만 입력' : null"
                           aria-label="쿠팡 쇼핑 아이디" v-model.trim="shopping.id" autocomplete="off"
                           :disabled="shopping.busy">
                </div>
                <div class="shopping-account__pw">
                    <input type="password" class="form-control form-control-sm shopping-account__input"
                           :placeholder="shopping.status && shopping.status.hasCredentials ? '저장됨 · 바꿀 때만 입력' : '쿠팡 쇼핑 비밀번호'"
                           aria-label="쿠팡 쇼핑 비밀번호" v-model="shopping.pw" autocomplete="new-password"
                           :disabled="shopping.busy" @keyup.enter="saveShopping">
                </div>
                <div class="shopping-account__acts">
                    <!-- 저장은 입력했을 때만 · 삭제는 ⋯ -->
                    <b-button v-if="shopping.id || shopping.pw || shopping.busy" size="sm" variant="primary"
                              :disabled="shopping.busy || !shopping.id || !shopping.pw"
                              @click="saveShopping">저장</b-button>
                    <b-dropdown size="sm" variant="outline-secondary" right no-caret boundary="window"
                                toggle-class="account-row__more" menu-class="account-row__menu"
                                :disabled="shopping.busy || !(shopping.status && shopping.status.hasCredentials)">
                        <template #button-content>
                            <i class="ri-more-fill" aria-hidden="true"></i><span class="sr-only">쿠팡 쇼핑 더보기</span>
                        </template>
                        <b-dropdown-item-button variant="danger" @click="removeShopping">삭제</b-dropdown-item-button>
                    </b-dropdown>
                </div>
                <!-- 저장된 아이디는 가린 채로만 받아 와서 칸을 미리 채울 수 없다 — 한 칸만 채웠을 때 둘 다 넣어야 저장되는 걸 알린다 -->
                <div v-if="shoppingNeedsBoth" class="shopping-account__hint">아이디·비밀번호를 모두 입력해야 저장됩니다</div>
                <div v-if="shopping.message" class="shopping-account__message"
                     :class="{ 'shopping-account__message--bad': shopping.messageBad }">{{ shopping.message }}</div>
            </section>

            <!-- 1688 로그인 계정 — 쿠팡 쇼핑 계정과 같은 꼴. 이 PC 공통 1688 계정 하나(위 판매자 계정마다 따로가 아니다), 이 PC 에만 암호화해 둔다 -->
            <section v-if="can1688()" class="shopping-account" role="listitem" aria-labelledby="alibaba1688AccountTitle">
                <div class="shopping-account__head">
                <div class="shopping-account__title">
                    <h6 id="alibaba1688AccountTitle" class="shopping-account__name">1688</h6>
                    <span v-b-tooltip.hover class="account-manager__q" tabindex="0" role="img"
                          aria-label="이 PC 공통 1688 계정 하나 — 판매자 계정마다 따로 저장되지 않습니다. 이 PC 에만 암호화해 저장됩니다. 저장하면 크롬에 1688 로그인 창을 띄워 저장한 정보로 자동 로그인합니다. 보안 확인(캡차)·2단계가 뜰 때만 그 창에서 직접 풀어 주세요. 협상 도구가 그 로그인을 같이 씁니다."
                          title="이 PC 공통 1688 계정 하나 — 판매자 계정마다 따로 저장되지 않습니다. 이 PC 에만 암호화해 저장됩니다. 저장하면 크롬에 1688 로그인 창을 띄워 저장한 정보로 자동 로그인합니다. 보안 확인(캡차)·2단계가 뜰 때만 그 창에서 직접 풀어 주세요. 협상 도구가 그 로그인을 같이 씁니다.">?</span>
                </div>
                <div class="shopping-account__sub">이 PC 만</div>
                </div>
                <div class="shopping-account__status">
                    <span :class="['shopping-account__bd', 'shopping-account__bd--' + aliLight.tone]" role="status">
                        <span v-if="toneMark[aliLight.tone]" aria-hidden="true">{{ toneMark[aliLight.tone] }}</span>
                        {{ aliLight.text }}
                    </span>
                </div>
                <div class="shopping-account__id">
                    <input type="text" class="form-control form-control-sm shopping-account__input"
                           :placeholder="ali.status && ali.status.id ? ali.status.id : '1688 아이디'"
                           :title="ali.status && ali.status.id ? '저장된 아이디 ' + ali.status.id + ' — 바꿀 때만 입력' : null"
                           aria-label="1688 아이디" v-model.trim="ali.id" autocomplete="off"
                           :disabled="ali.busy">
                </div>
                <div class="shopping-account__pw">
                    <input type="password" class="form-control form-control-sm shopping-account__input"
                           :placeholder="aliSaved ? '저장됨 · 바꿀 때만 입력' : '1688 비밀번호'"
                           aria-label="1688 비밀번호" v-model="ali.pw" autocomplete="new-password"
                           :disabled="ali.busy" @keyup.enter="saveAli">
                </div>
                <div class="shopping-account__acts">
                    <b-button v-if="ali.id || ali.pw || ali.busy" size="sm" variant="primary"
                              :disabled="ali.busy || !ali.id || !ali.pw"
                              @click="saveAli">저장</b-button>
                    <!-- 저장된 계정으로 자동 로그인 1번(비밀번호 다시 입력 0 · 먼저 확인창) — 캡차·2단계만 사람 -->
                    <b-button size="sm" variant="outline-primary" :disabled="ali.busy || !aliSaved || aliLoginRunning"
                              title="저장된 1688 계정으로 자동 로그인합니다 (먼저 확인창이 뜹니다)"
                              @click="loginAli">로그인 확인</b-button>
                    <b-dropdown size="sm" variant="outline-secondary" right no-caret boundary="window"
                                toggle-class="account-row__more" menu-class="account-row__menu"
                                :disabled="ali.busy || !aliSaved">
                        <template #button-content>
                            <i class="ri-more-fill" aria-hidden="true"></i><span class="sr-only">1688 더보기</span>
                        </template>
                        <b-dropdown-item-button variant="danger" @click="removeAli">삭제</b-dropdown-item-button>
                    </b-dropdown>
                </div>
                <!-- 캡차·실패 까닭은 CLI 가 사람 말로 준다(비번·쿠키 값 없음) -->
                <div v-if="aliDetail" class="shopping-account__message"
                     :class="{ 'shopping-account__message--bad': ali.status && ali.status.state === 'failed' }">{{ aliDetail }}</div>
                <div v-if="ali.message" class="shopping-account__message"
                     :class="{ 'shopping-account__message--bad': ali.messageBad }" role="status">
                    {{ ali.message }}
                    <b-button v-if="ali.removePending" size="sm" variant="link" class="p-0 ml-1 align-baseline"
                              :disabled="ali.busy" @click="rereadAliAfterRemove">다시 읽기</b-button>
                </div>
            </section>

            <!-- 카카오톡 — 자리만(판 #776: 로그인은 «설정» 단추 → CLI 가 띄우는 로그인 창 · 아이디·비밀번호 칸 없음). 기능이 붙기 전엔 단추를 막아 둔다 -->
            <section class="shopping-account" role="listitem" aria-labelledby="kakaoAccountTitle">
                <div class="shopping-account__head">
                <h6 id="kakaoAccountTitle" class="shopping-account__title">카카오톡</h6>
                <div class="shopping-account__sub">보고·명령</div>
                </div>
                <div class="shopping-account__status">
                    <span class="shopping-account__bd shopping-account__bd--none">준비 중</span>
                </div>
                <div class="shopping-account__acts">
                    <b-button size="sm" variant="outline-secondary" class="account-row__btn" disabled
                              title="카카오톡 연결은 준비 중입니다">설정</b-button>
                </div>
            </section>
            </div>
        </div>

        <!-- 바닥줄 = 로그아웃 -->
        <template #modal-footer>
            <b-button size="sm" variant="link" class="account-manager__logout" :disabled="saving" @click="$emit('logout')">로그아웃</b-button>
        </template>

        <!-- 계정별 자동수집 설정 — 창 하나를 두고 카드 ⋯ 메뉴의 「자동수집 설정」이 그 계정으로 연다.
             b-modal 은 겹쳐 뜬다(이 창 위에 뜨고, 닫아도 이 창은 그대로). -->
        <AutoCollectModal
            :suppliercode="autoTarget ? autoTarget.suppliercode : ''"
            :companyid="autoTarget ? autoTarget.companyid : ''"
            :email="autoTarget ? autoTarget.email : ''"
            :collect-auth="autoTarget ? autoTarget.collectAuth : null"
            :other-suppliercodes="otherSuppliercodes"
            @status-update="(m) => $emit('auto-collect-status', m)"
            @saved="onAutoCollectSaved"
        />
        <!-- 계정 카드 ⋯ 메뉴의 「Open API 설정」 — 그 계정 전용 작은 창(키는 이 PC 의 couplus 에만) -->
        <OpenApiKeyModal :row="openApiTarget" @changed="(m) => (openApiKeys = m)" />
    </b-modal>
</template>

<script>
import axios from 'axios';
import CoupangAccountRow from '@/components/common/CoupangAccountRow.vue';
import AutoCollectModal from '@/components/common/AutoCollectModal.vue';
import OpenApiKeyModal from '@/components/common/OpenApiKeyModal.vue';
import {
    askExtension, carryOver, loadCoupangAccounts, findCoupangFor, checkRowLogin, makeRow, makeSwitchConfirm, newRowKey, saveRow, setRowMessage,
    loadShoppingAccount, saveShoppingAccount, removeShoppingAccount, shoppingStatusOf, TONE_MARK,
    loadAccountSync, saveAccountSync, loadOpenApiKeys,
    get1688Account, save1688Account, remove1688Account, login1688Account, ali1688StatusOf, ali1688Pending,
} from '@/services/coupangAccountFlow';

// 쿠팡 쇼핑 계정 상태 다시 읽기 간격·횟수(저장 직후 로그인이 끝날 때까지)
// CLI 로그인은 추가 인증을 150초까지 기다린다(couplus-cli/core/shop/login.js timeoutMs) — 그보다 길게 본다.
// 짧으면 결과가 나기 전에 멈춰 「로그인 중」에 그대로 멈춰 보인다(2026-09-24 실측: 120초에 멈춤).
const SHOPPING_POLL_MS = 3000;
const SHOPPING_POLL_MAX = 70;
const SHOPPING_CHECKING_MSG = '저장함 · 로그인을 확인하고 있습니다.';
// 1688 로그인 다시 읽기 — 확장 로그인 탭은 보안 확인(캡차)을 사람이 풀 때까지 10분 기다린다(background.js ALI1688_HUMAN_WAIT_MS) — 그보다 길게
const ALI_POLL_MS = 3000;
const ALI_POLL_MAX = 220;

export default {
    name: 'CoupangAccountModal',
    components: { CoupangAccountRow, AutoCollectModal, OpenApiKeyModal },
    props: {
        // 수집·저장 중이면 전환을 막는 이유(헤더가 계산). 빈 문자열이면 전환 가능
        switchBlockedReason: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            // 쿠팡 Open API 키 상태(이 PC · 판매자코드 → 상태). null = 확장·CLI 응답 없음
            openApiKeys: null,
            openApiTarget: null,
            rows: [],
            loading: false,
            loadError: '',
            saving: false,
            toneMark: TONE_MARK,
            shopping: {
                id: '',
                pw: '',
                status: null,
                busy: false,
                message: '',
                messageBad: false,
            },
            shoppingTimer: null,
            shoppingPolls: 0,
            aliTimer: null,
            aliPolls: 0,
            // 1688 로그인 계정 — 이 PC 공통 하나(판매자 계정별 아님). 저장 여부와 아이디(가림)만 받아 온다
            ali: {
                id: '',
                pw: '',
                status: null,
                busy: false,
                message: '',
                messageBad: false,
                removePending: false, // 지우기가 10초 안에 답하지 않음 — 뒤에서 계속 지우는 중
            },
            autoTarget: null,      // 자동수집 창을 연 줄
            accountSync: null,     // 함께 전환 스위치. null = 확장이 모름(토글 숨김)
            accountSyncBusy: false,
        };
    },
    computed: {
        aliSaved() {
            return !!(this.ali.status && this.ali.status.ok && this.ali.status.hasCredentials);
        },
        // «로그인 확인» 은 로그인이 도는 중(로그인 중·캡차 기다림)일 때만 막는다 — «저장됨 · 로그인 전»(데몬 다시 시작 뒤)엔 눌려야 한다
        aliLoginRunning() {
            return ali1688Pending(this.ali.status);
        },
        aliLight() {
            return ali1688StatusOf(this.ali.status);
        },
        aliDetail() {
            const st = this.ali.status;
            return st && st.ok && st.hasCredentials && st.message ? st.message : '';
        },
        shoppingLight() {
            return shoppingStatusOf(this.shopping.status);
        },
        // 저장 버튼이 입력 부족으로 잠겨 있을 때(한 칸만 채움)만 안내 — 늘 띄우지 않는다
        shoppingNeedsBoth() {
            const s = this.shopping;
            if (s.busy) return false;
            return !!s.id !== !!s.pw;
        },
        // 같은 시각 겹침 경고용 — 자동수집 창을 연 계정 말고 나머지 판매자코드
        otherSuppliercodes() {
            const mine = this.autoTarget ? this.autoTarget.suppliercode : '';
            return [...new Set(this.rows.map(r => r.suppliercode).filter(sc => sc && sc !== mine))];
        },
    },
    beforeDestroy() {
        this.stopShoppingPoll();
        this.stopAliPoll();
    },
    methods: {
        onShow() {
            // 창을 다시 열면 지난 확인 결과 대신 지금 세션 상태를 보여준다(입력 중인 값은 남긴다)
            this.rows.forEach((r) => { r.result = null; r.message = ''; r.messageBad = false; });
            this.load();
            this.loadAccountSync();
            this.shopping.message = '';
            this.startShoppingPoll();
            this.loadOpenApi();
            this.ali.message = '';
            this.ali.removePending = false;
            this.startAliPoll();
        },
        // ── 쿠팡 Open API 키 (계정 줄마다) — 도구가 dev 인 동안 개발자 + 소싱관리(shownomad) 계정에만 보인다 ──
        canOpenApi() {
            const auth = (this.$store.state.user && this.$store.state.user.auth) || {};
            return !!(auth.showdeveloper && auth.shownomad);
        },
        async loadOpenApi() {
            if (!this.canOpenApi()) return;
            try {
                this.openApiKeys = await loadOpenApiKeys();
            } catch (e) {
                this.openApiKeys = null;
            }
        },
        /** 줄에 줄 상태 — 권한 밖이면 undefined(버튼·표시 숨김) · 확장 응답 없으면 «안 됨» */
        openApiFor(row) {
            if (!this.canOpenApi() || !row || !row.suppliercode) return undefined;
            const code = String(row.suppliercode).toUpperCase();
            return (this.openApiKeys && this.openApiKeys[code]) || { registered: false };
        },
        openOpenApi(row) {
            this.openApiTarget = row;
            this.$nextTick(() => this.$bvModal.show('openApiKeyModal'));
        },
        // ── 1688 로그인 계정 — 1688 협상 도구(nomadSourcing) 몫. 도구가 dev 인 동안 개발자 + 소싱관리(shownomad) 계정에만 보인다 ──
        //    도구를 open 으로 바꾸면 shownomad 만 남긴다(tool-catalog/growth.js 머리말 · 판 #833)
        can1688() {
            const auth = (this.$store.state.user && this.$store.state.user.auth) || {};
            return !!(auth.showdeveloper && auth.shownomad);
        },
        async refreshAli() {
            try {
                this.ali.status = await get1688Account();
            } catch (e) {
                console.error('[멀티계정관리] 1688 계정 상태 확인 실패', e);
                this.ali.status = { ok: false, error: '상태를 확인하지 못했습니다' };
            }
            return this.ali.status;
        },
        /** 지금 한 번 읽고, 로그인 중·캡차 대기면 끝날 때까지 다시 읽는다(쇼핑 계정 폴링과 같은 꼴) */
        startAliPoll() {
            this.stopAliPoll();
            if (!this.can1688()) return;
            this.aliPolls = 0;
            const tick = async () => {
                this.aliTimer = null;
                const st = await this.refreshAli();
                this.aliPolls += 1;
                // 저장 직후엔 로그인이 막 시작돼 아직 상태가 안 바뀌었을 수 있다 — 처음 몇 번은 더 본다
                const pending = ali1688Pending(st) || (this.aliPolls < 3 && st && st.ok && st.hasCredentials && st.state !== 'logged-in' && st.state !== 'failed');
                if (pending && this.aliPolls < ALI_POLL_MAX) this.aliTimer = setTimeout(tick, ALI_POLL_MS);
            };
            tick();
        },
        stopAliPoll() {
            if (this.aliTimer) clearTimeout(this.aliTimer);
            this.aliTimer = null;
        },
        async saveAli() {
            if (this.ali.busy || !this.ali.id || !this.ali.pw) return;
            this.ali.busy = true;
            this.ali.message = '';
            try {
                const r = await save1688Account(this.ali.id, this.ali.pw);
                this.ali.messageBad = !(r && r.ok);
                this.ali.message = r && r.ok ? '저장함 · 1688 로그인을 시작했습니다' : ((r && r.error) || '저장하지 못했습니다.');
                if (r && r.ok) this.ali.id = '';
            } catch (e) {
                console.error('[멀티계정관리] 1688 계정 저장 실패', e);
                this.ali.message = '저장하지 못했습니다.';
                this.ali.messageBad = true;
            } finally {
                this.ali.pw = ''; // 성공·실패 모두 — 비밀번호를 화면에 남겨 두지 않는다
                this.ali.busy = false;
            }
            this.startAliPoll(); // 저장 = 뒤에서 1688 자동 로그인 시작 — 끝날 때까지 다시 읽는다
        },
        /** «로그인 확인» — 저장된 계정으로 1688 자동 로그인 1번(비밀번호 다시 입력 0 · 캡차·2단계만 사람). 끝날 때까지 상태를 다시 읽는다 */
        async loginAli() {
            if (this.ali.busy || !this.aliSaved) return;
            if (!confirm('저장된 1688 계정으로 로그인할까요?\n크롬에 1688 로그인 화면이 열려 자동으로 로그인합니다. 보안 확인(캡차)·2단계가 뜨면 그 화면에서 직접 풀어 주세요.')) return;
            this.ali.busy = true;
            this.ali.message = '';
            try {
                const r = await login1688Account();
                this.ali.messageBad = !(r && r.ok);
                this.ali.message = r && r.ok ? '1688 로그인을 시작했습니다' : ((r && r.error) || '로그인을 시작하지 못했습니다.');
            } catch (e) {
                console.error('[멀티계정관리] 1688 로그인 확인 실패', e);
                this.ali.message = '로그인을 시작하지 못했습니다.';
                this.ali.messageBad = true;
            } finally {
                this.ali.busy = false;
            }
            this.startAliPoll();
        },
        async removeAli() {
            if (this.ali.busy) return;
            if (!confirm('1688 로그인 정보를 지울까요?\n판매자 협상 도구가 1688 에 로그인하지 못하게 되고, 이 PC 의 1688 로그인(쿠키)도 지워집니다.')) return;
            this.ali.busy = true;
            this.ali.removePending = false;
            this.stopAliPoll();
            // 크롬을 멈추고 프로필까지 지우느라 10초를 넘길 수 있다 — 누른 즉시 기다림을 보인다
            this.ali.message = '지우는 중…';
            this.ali.messageBad = false;
            try {
                const r = await remove1688Account();
                this.ali.removePending = !!(r && r.pending);
                this.ali.message = r && r.ok ? '지움' : ((r && r.error) || '지우지 못했습니다.');
                this.ali.messageBad = !(r && r.ok) && !this.ali.removePending;
            } catch (e) {
                console.error('[멀티계정관리] 1688 계정 삭제 실패', e);
                this.ali.message = '지우지 못했습니다.';
                this.ali.messageBad = true;
            } finally {
                this.ali.busy = false;
            }
            this.stopAliPoll();
            await this.refreshAli();
        },
        /** 지우기가 늦게 끝날 때 — 상태를 다시 읽어 저장이 비었으면 「지움」, 프로필 실패면 CLI 사람 말 그대로 */
        async rereadAliAfterRemove() {
            if (this.ali.busy) return;
            this.ali.busy = true;
            try {
                const st = await this.refreshAli();
                // 도는 로그인을 아직 멈추는 중이면(state logging-in·needs-captcha) 「지우는 중」 그대로
                if (st && st.ok && !st.hasCredentials && !ali1688Pending({ ...st, hasCredentials: true })) {
                    const profileFailed = st.state === 'failed' && st.reason === 'profile-failed';
                    this.ali.removePending = false;
                    this.ali.message = profileFailed ? (st.message || '지우지 못했습니다.') : '지움';
                    this.ali.messageBad = profileFailed;
                }
            } finally {
                this.ali.busy = false;
            }
        },
        // ── 쿠팡 쇼핑 공통 계정 ──
        async refreshShopping() {
            try {
                this.shopping.status = await loadShoppingAccount();
            } catch (e) {
                console.error('[멀티계정관리] 쿠팡 쇼핑 계정 상태 조회 실패', e);
                this.shopping.status = { ok: false, error: '상태를 확인하지 못했습니다' };
            }
            return this.shopping.status;
        },
        /** 지금 한 번 읽고, 로그인 중이면 끝날 때까지 몇 초 간격으로 다시 읽는다 */
        startShoppingPoll() {
            this.stopShoppingPoll();
            this.shoppingPolls = 0;
            const tick = async () => {
                this.shoppingTimer = null;
                const st = await this.refreshShopping();
                this.shoppingPolls += 1;
                // 저장 직후엔 로그인이 막 시작돼 아직 「로그인 중」이 아닐 수 있다 — 결과가 날 때까지 더 본다
                const pending = st && st.ok
                    && (st.loggingIn || (st.hasCredentials && !(st.session && st.session.ready) && !st.lastError));
                if (pending && this.shoppingPolls < SHOPPING_POLL_MAX) {
                    this.shoppingTimer = setTimeout(tick, SHOPPING_POLL_MS);
                } else if (!pending && this.shopping.message === SHOPPING_CHECKING_MSG) {
                    // 결과가 났다 — 「확인하고 있습니다」를 상태 표시(로그인됨/실패)와 같이 남기지 않는다
                    this.shopping.message = '';
                }
            };
            tick();
        },
        stopShoppingPoll() {
            if (this.shoppingTimer) clearTimeout(this.shoppingTimer);
            this.shoppingTimer = null;
        },
        async saveShopping() {
            if (this.shopping.busy || !this.shopping.id || !this.shopping.pw) return;
            this.shopping.busy = true;
            this.shopping.message = '';
            let saved = false;
            try {
                const r = await saveShoppingAccount(this.shopping.id, this.shopping.pw);
                if (!r.ok) {
                    this.shopping.message = r.error || '저장하지 못했습니다.';
                    this.shopping.messageBad = true;
                } else {
                    saved = true;
                    this.shopping.id = '';
                    this.shopping.pw = '';
                    this.shopping.message = SHOPPING_CHECKING_MSG;
                    this.shopping.messageBad = false;
                }
            } catch (e) {
                console.error('[멀티계정관리] 쿠팡 쇼핑 계정 저장 실패', e);
                this.shopping.message = '저장하지 못했습니다.';
                this.shopping.messageBad = true;
            } finally {
                this.shopping.busy = false;
            }
            if (saved) this.startShoppingPoll();
        },
        async removeShopping() {
            if (this.shopping.busy) return;
            if (!confirm(`쿠팡 쇼핑 계정을 삭제할까요?
상품검색·리뷰 등 공통 기능의 로그인이 풀립니다.`)) return;
            this.shopping.busy = true;
            this.shopping.message = '';
            this.stopShoppingPoll();
            try {
                const r = await removeShoppingAccount();
                this.shopping.message = r.ok ? '삭제함' : (r.error || '삭제하지 못했습니다.');
                this.shopping.messageBad = !r.ok;
            } catch (e) {
                console.error('[멀티계정관리] 쿠팡 쇼핑 계정 삭제 실패', e);
                this.shopping.message = '삭제하지 못했습니다.';
                this.shopping.messageBad = true;
            } finally {
                this.shopping.busy = false;
            }
            await this.refreshShopping();
        },
        // ── 목록: 쿠플러스 연결 계정 × 확장의 쿠팡 로그인 ──
        async load() {
            this.loading = true;
            this.loadError = '';
            const drafts = this.rows.filter(r => r.isNew);
            let linked = [];
            try {
                const res = await axios.post('/api/accountLink/list', {});
                linked = res.data && res.data.res === 'success' ? (res.data.accounts || []) : [];
            } catch (e) {
                console.error('[멀티계정관리] 쿠플러스 계정 목록 조회 실패', e);
                this.loadError = '계정 목록을 불러오지 못했습니다.';
            }
            const accounts = await loadCoupangAccounts();
            if (!accounts) this.loadError = '쿠플러스 확장에서 응답이 없습니다. 먼저 페이지를 새로고침해 보세요(확장을 다시 올린 뒤에는 열려 있던 탭이 끊깁니다). 그래도 안 되면 확장을 최신 버전(10.86 이상)으로 업데이트해 주세요. 계정 전환은 지금도 됩니다.';
            const coupang = accounts || [];
            const used = new Set();

            const rows = linked.map((acc) => {
                // 자동수집 창과 같은 잣대(findCoupangFor) — 판매자코드, 옛 서버면 회사ID 가 하나로 맞을 때만
                const cp = findCoupangFor(coupang, acc);
                if (cp) used.add(cp.suppliercode);
                // 옛 서버는 판매자코드를 안 준다 — 지금 사용 중인 계정은 로그인 정보에 있는 값을 쓴다
                const mine = acc.isCurrent ? ((this.$store.state.user && this.$store.state.user.suppliercode) || '') : '';
                return makeRow({
                    email: acc.email,
                    name: acc.name,
                    companyid: acc.companyid,
                    suppliercode: acc.suppliercode || (cp && cp.suppliercode) || mine,
                    isOwner: acc.isOwner,
                    isCurrent: acc.isCurrent,
                    // 자동수집 창이 그 계정 권한으로 항목을 켜고 끈다(없으면 로그인한 사람 권한으로 떨어졌다)
                    collectAuth: acc.collectAuth,
                    lastCollect: acc.lastCollect,
                }, cp);
            });
            // 쿠플러스 계정과 이어지지 않은 쿠팡 로그인도 보여 준다(지울 수 있어야 한다)
            coupang.filter(c => !used.has(c.suppliercode)).forEach((c) => {
                rows.push(makeRow({ suppliercode: c.suppliercode, companyid: c.companyid }, c));
            });
            // 입력 중이던 비밀번호·방금 결과를 살린다
            this.rows = carryOver(rows, this.rows).concat(drafts);
            this.loading = false;
        },
        addRow() {
            const row = makeRow({}, null);
            row.isNew = true;
            row.key = newRowKey();
            this.rows.push(row);
        },
        /** 그 줄 저장 — 쿠팡 아이디·비밀번호만 저장한다(쿠키·탭·로그인 0). 로그인 확인은 checkOne */
        async saveOne(row) {
            if (this.saving) return;
            this.saving = true;
            try {
                await saveRow(row);
            } finally {
                this.saving = false;
            }
            await this.load();
        },
        /** 그 줄 쿠팡 로그인 확인 — 항상 확인창 먼저. 다른 계정이 로그인돼 있으면 확장이 보관한 뒤 전환한다 */
        async checkOne(row) {
            if (this.saving) return;
            this.saving = true;
            try {
                await checkRowLogin(row, {
                    confirmSwitch: makeSwitchConfirm(this, (code) => {
                        const hit = this.rows.find(r => r.suppliercode === code);
                        return hit ? (hit.companyid || hit.suppliercode) : code;
                    }),
                });
            } finally {
                this.saving = false;
            }
            // 쿠팡 쪽이 이 계정으로 바뀌었다(로그인 확인 성공) → 쿠플러스도 따라 바꾼다(스위치 켜짐·연결된 계정일 때만)
            const coupangSwitched = !!(row.result && !row.messageBad && row.suppliercode);
            if (coupangSwitched && this.accountSync === true && row.email && !row.isCurrent && !this.switchBlockedReason) {
                this.$emit('switch-account', row.email, { skipCoupangSync: true });
                return;
            }
            await this.load();
        },
        // ── 쿠플러스·쿠팡 계정 함께 전환 ──
        async loadAccountSync() {
            try {
                this.accountSync = await loadAccountSync();
            } catch (e) {
                console.error('[멀티계정관리] 함께 전환 설정 조회 실패', e);
                this.accountSync = null;
            }
        },
        async toggleAccountSync(enabled) {
            this.accountSyncBusy = true;
            const before = this.accountSync;
            this.accountSync = !!enabled;
            try {
                if (!(await saveAccountSync(enabled))) {
                    this.accountSync = before;
                    alert('설정을 저장하지 못했습니다. 잠시 후 다시 시도해주세요.');
                }
            } catch (e) {
                console.error('[멀티계정관리] 함께 전환 설정 저장 실패', e);
                this.accountSync = before;
            } finally {
                this.accountSyncBusy = false;
            }
        },
        // ── 자동수집 ──
        openAutoCollect(row) {
            if (!row.suppliercode || !row.companyid) return;
            this.autoTarget = row;
            // props 가 들어간 다음에 연다 — 창이 열릴 때 그 계정 설정을 읽는다
            this.$nextTick(() => this.$bvModal.show('autoCollectSettingsModal'));
        },
        onAutoCollectSaved(e) {
            // 새 확장은 저장 완료를 알려준다 → 바로 다시 읽는다. 구 확장은 알려주지 않으니 잠깐 뒤 다시 읽는다
            setTimeout(() => {
                this.load();
                this.$emit('auto-collect-change');
            }, e && e.confirmed ? 0 : 800);
        },
        // ── 전환 · 삭제 ──
        switchTo(row) {
            if (this.switchBlockedReason) return;
            // 판매자코드를 같이 넘긴다 — 함께 전환이 켜져 있으면 헤더가 쿠팡 계정도 맞춘다
            this.$emit('switch-account', row.email, { suppliercode: row.suppliercode, companyid: row.companyid });
        },
        async removeRow(row) {
            if (row.isNew) {
                this.rows = this.rows.filter(r => r !== row);
                return;
            }
            const label = row.companyid || row.email || row.suppliercode;
            if (!confirm(`${label} 계정을 목록에서 삭제할까요?
저장된 쿠팡 로그인 정보와 자동수집 예약도 함께 지워집니다.`)) return;
            try {
                if (row.suppliercode && (row.cpHasCred || row.cpId)) {
                    const r = await askExtension('removeCoupangAccount', { suppliercode: row.suppliercode }, 'coupangAccountRemoved', 8000);
                    if (!r || !r.success) {
                        setRowMessage(row, '쿠팡 로그인 정보를 지우지 못했습니다.', true);
                        return;
                    }
                }
                // 쿠플러스 연결은 원래 계정·사용 중인 계정이 아니면 같이 해제한다
                if (row.email && !row.isOwner && !row.isCurrent) {
                    const res = await axios.post('/api/accountLink/remove', { email: row.email });
                    if (!res.data || res.data.res !== 'success') {
                        setRowMessage(row, (res.data && res.data.alert) || '쿠플러스 계정 연결을 해제하지 못했습니다.', true);
                        return;
                    }
                }
            } catch (e) {
                console.error('[멀티계정관리] 삭제 실패', e);
                setRowMessage(row, '삭제하지 못했습니다.', true);
                return;
            }
            await this.load();
        },
    },
};
</script>

<style lang="scss">
// 앱 테마 변수(값만 가져온다 — CSS 를 내지 않는 파일들)
@import '~@/assets/scss/bootstrap/functions';
@import '~@/assets/scss/custom_variable';
@import '~@/assets/scss/variable';

// b-modal 대화상자는 컴포넌트 밖에 그려져 scoped 로는 못 닿는다 — 폭만 여기서 넓힌다
.account-manager-dialog {
    max-width: min(1440px, 96vw);

    // 카드가 떠 보이게 몸통만 옅은 회색
    .modal-body { background: #f7f9fb; }
    .modal-footer { padding: 8px 16px; }
}
.account-manager__head {
    align-items: center;

    .close { margin: -1rem -1rem -1rem 0; }
}

// 다크 테마 = body.dark (ModeSwitch 가 켠다). .modal-content·.form-control·.table 기본은
// assets/scss/backend/layouts/_dark.scss 가 칠하는데, 표 글씨(보라)·입력칸 글씨가 배경과 너무 가깝다 —
// 이 창에서만 같은 테마 변수로 대비를 올린다. 라이트 테마는 건드리지 않는다.
body.dark .account-manager-dialog {
    .modal-content {
        border: 1px solid $dark-border-color;
    }
    .modal-header,
    .modal-footer {
        border-color: $dark-border-color;
    }
    .close {
        color: $gray-200;
        text-shadow: none;
    }
    .table {
        color: $gray-200;
        th,
        td {
            color: $gray-200;
            border-color: $dark-border-color;
        }
    }
    .form-control {
        color: $gray-200;
        background: $dark-gray;
        border-color: $gray-700;
        &::placeholder {
            color: $gray-600;
        }
        &:focus {
            border-color: $primary;
        }
    }
    .text-muted {
        color: $gray-500 !important;
    }
    .modal-body { background: $dark-body-bg; }
    .account-manager__sub,
    .account-manager__privacy,
    .account-manager__sec-title,
    .account-manager__empty,
    .account-manager__add,
    .account-manager__q,
    .shopping-account__sub,
    .shopping-account__message {
        color: $gray-500;
    }
    .account-manager__add,
    .account-manager__q { border-color: $gray-700; }
    .shopping-account {
        background: $dark-gray;
        border-color: $dark-border-color;
    }
    // 공통 로그인 상태 배지 — 판매자 카드 배지(CoupangAccountRow.vue body.dark .account-row__bd)와 같은 색
    .shopping-account__bd { background: $dark-secondary; color: $gray-400; }
    .shopping-account__bd--ok { background: $dark-success; color: $success; }
    .shopping-account__bd--bad { background: $dark-danger; color: $danger; }
    .shopping-account__bd--wait { background: $dark-warning; color: $warning; }
    .account-manager__error {
        color: $danger;
    }
}
</style>

<style lang="scss" scoped>
// 정리안 B «카드 줄» — 계정 하나 = 카드 한 장(카드 모양은 CoupangAccountRow.vue). 여기는 창 머리줄·칸 제목·공통 로그인 두 장
.account-manager {
    margin: -4px 0 0;

    // 390 에서 스위치 글에 밀려 «멀티계정관 / 리» 로 낱말 중간이 끊겼다 — 제목은 한 줄로 두고 스위치 글이 낱말 단위로 접힌다
    &__title {
        flex: 1 0 auto;
        margin: 0 8px 0 0;
        white-space: nowrap;
    }

    &__sync {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
        margin-right: 12px;
        font-size: 13px;
        word-break: keep-all;
    }

    // 설명 문장 대신 ? — 마우스를 올리거나 탭으로 오면 툴팁
    &__q {
        display: inline-grid;
        place-items: center;
        width: 16px;
        height: 16px;
        border: 1px solid #cfd5de;
        border-radius: 50%;
        font-size: 10.5px;
        font-weight: 700;
        line-height: 1;
        color: #6b7585;
        cursor: help;
        vertical-align: 1px;

        &:focus-visible {
            outline: 2px solid #1a73e8;
            outline-offset: 1px;
        }
    }

    &__error {
        color: #b3261e;
        font-size: 13px;
    }

    &__privacy {
        margin: 0 0 10px;
        font-size: 12px;
        color: #6b7585;
        word-break: keep-all;
    }

    &__sec {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 4px 0 8px;

        &--shared { margin-top: 18px; }
    }

    &__sec-title {
        margin: 0;
        font-size: 13px;
        font-weight: 700;
        color: #4a5566;
    }

    &__empty {
        margin: 0 0 8px;
        padding: 12px 14px;
        font-size: 13px;
        color: #6b7585;
    }

    &__add {
        width: 100%;
        padding: 8px;
        border: 1px dashed #cfd5de;
        border-radius: 10px;
        background: transparent;
        font-size: 13px;
        font-weight: 600;
        color: #4a5566;
        transition: border-color 0.15s ease-out, color 0.15s ease-out;

        &:hover:not(:disabled) { border-color: #1a73e8; color: #1a73e8; }
        &:focus-visible { outline: 2px solid #1a73e8; outline-offset: 1px; }
        &:disabled { opacity: 0.6; cursor: default; }
    }

    &__sub {
        font-size: 12px;
        color: #5f6368;
    }

    // 공통 로그인 = 판매자 카드처럼 한 줄씩 쌓는다(두 칸 나란히 → 한 줄씩 · 판 #833)
    &__shared {
        display: grid;
        gap: 8px;
    }

    &__logout {
        margin-right: auto;
        padding-left: 0;
        color: #6b7585;
    }
}

// 공통 로그인 줄(쿠팡 쇼핑 · 1688 · 카카오톡) — 판매자 카드(CoupangAccountRow.vue account-row--card)와 같은 칸 폭·같은 자리
.shopping-account {
    display: grid;
    grid-template-columns: minmax(140px, 180px) minmax(200px, 1fr) minmax(120px, 170px) minmax(140px, 190px) auto;
    grid-template-areas: "head status id pw acts";
    gap: 8px 14px;
    align-items: center;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #e3e7ed;
    border-radius: 10px;

    &__head { grid-area: head; min-width: 0; }
    &__status { grid-area: status; min-width: 0; }
    &__id { grid-area: id; min-width: 0; }
    &__pw { grid-area: pw; min-width: 0; }

    &__title {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 0;
        font-size: 14px;
        font-weight: 600;
    }

    &__name {
        margin: 0;
        font-size: inherit;
        font-weight: inherit;
    }

    &__sub {
        font-size: 12px;
        color: #5f6368;
    }

    // 칸이 좁아 «아이디 다시 입력 (저장: f» 처럼 잘렸다 — 안내는 저장된 아이디만 · 그래도 길면 … 로 끝을 알린다
    &__input {
        text-overflow: ellipsis;
    }

    &__acts {
        grid-area: acts;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
        white-space: nowrap;
    }

    // 상태 = 판매자 카드 배지와 같은 꼴(account-row__bd) · 낱말 단위로만 접는다
    &__bd {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 9px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.5;
        word-break: keep-all;
        color: #4a5566;
        background: #eef1f5;

        &--ok { color: #1d7a45; background: #e6f5ec; }
        &--bad { color: #b3261e; background: #fdeceb; }
        &--wait { color: #8f5506; background: #fdf3e2; }
    }

    &__hint,
    &__message {
        grid-column: 1 / -1;
        font-size: 12px;
        color: #5f6368;
    }

    &__hint { color: #8f5506; }
    &__message--bad { color: #b3261e; }

    // 판매자 카드와 같은 꺾임(1100 = 두 줄 · 640 = 한 칸씩)
    @media (max-width: 1100px) {
        grid-template-columns: minmax(140px, 1fr) minmax(120px, 1fr) minmax(140px, 1fr);
        grid-template-areas:
            "head status status"
            "id pw acts";
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        grid-template-areas: "head" "status" "id" "pw" "acts";

        .shopping-account__acts { justify-content: flex-start; flex-wrap: wrap; }
    }
}
</style>
